import {
  DEFAULT_INTERFACE_JSON,
  EXTERNAL_API_MODE,
  HTTP_METHODS,
} from '../constants/externalApi'

function generateExternalApiId() {
  return `ea_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`
}

function cloneDefaultInterfaceJson() {
  return {
    method: DEFAULT_INTERFACE_JSON.method,
    url: DEFAULT_INTERFACE_JSON.url,
    body: {},
    contentType: DEFAULT_INTERFACE_JSON.contentType,
  }
}

function normalizeHeaderRow(raw) {
  const row = raw && typeof raw === 'object' ? raw : {}
  return {
    name: row.name != null ? String(row.name) : '',
    value: row.value != null ? String(row.value) : '',
  }
}

function normalizeInputParamRow(raw) {
  const row = raw && typeof raw === 'object' ? raw : {}
  return {
    name: row.name != null ? String(row.name) : '',
    value: row.value != null ? String(row.value) : '',
    description: row.description != null ? String(row.description) : '',
  }
}

function parseInterfaceJsonValue(raw) {
  if (raw == null) return cloneDefaultInterfaceJson()
  if (typeof raw === 'string') {
    const text = raw.trim()
    if (!text) return cloneDefaultInterfaceJson()
    try {
      const parsed = JSON.parse(text)
      return parsed && typeof parsed === 'object' ? parsed : cloneDefaultInterfaceJson()
    } catch (e) {
      return { raw: text }
    }
  }
  if (typeof raw === 'object') return { ...raw }
  return cloneDefaultInterfaceJson()
}

/** 新建一条接口记录 */
export function createDefaultExternalApiItem(mode = EXTERNAL_API_MODE.JSON) {
  const resolvedMode = mode === EXTERNAL_API_MODE.FORM ? EXTERNAL_API_MODE.FORM : EXTERNAL_API_MODE.JSON
  return normalizeExternalApiItem({
    id: generateExternalApiId(),
    mode: resolvedMode,
    name: '',
    description: '',
    interface_json: cloneDefaultInterfaceJson(),
    method: 'POST',
    url: '',
    headers: [],
    input_params: [],
  })
}

/** 表单行 → 持久化结构 */
export function normalizeExternalApiItem(raw) {
  const item = raw && typeof raw === 'object' ? raw : {}
  const mode = item.mode === EXTERNAL_API_MODE.FORM ? EXTERNAL_API_MODE.FORM : EXTERNAL_API_MODE.JSON
  const method = HTTP_METHODS.includes(String(item.method || '').toUpperCase())
    ? String(item.method).toUpperCase()
    : 'POST'
  const headers = Array.isArray(item.headers) ? item.headers.map(normalizeHeaderRow) : []
  const inputParams = Array.isArray(item.input_params)
    ? item.input_params.map(normalizeInputParamRow)
    : Array.isArray(item.inputParams)
      ? item.inputParams.map(normalizeInputParamRow)
      : []

  return {
    id: item.id != null && String(item.id).trim() !== '' ? String(item.id) : generateExternalApiId(),
    name: item.name != null ? String(item.name) : '',
    description: item.description != null ? String(item.description) : '',
    mode,
    interface_json: parseInterfaceJsonValue(item.interface_json),
    method,
    url: item.url != null ? String(item.url) : '',
    headers,
    input_params: inputParams,
  }
}

/** 表单模式 → interface_json（仅用于展示回填 / 旧数据兼容，不写入 external_apis_form） */
export function buildInterfaceJsonFromForm(item) {
  const normalized = normalizeExternalApiItem(item)
  const body = {}
  normalized.input_params.forEach((row) => {
    const key = String(row.name || '').trim()
    if (!key) return
    body[key] = row.value != null ? String(row.value) : ''
  })
  const headers = {}
  normalized.headers.forEach((row) => {
    const key = String(row.name || '').trim()
    if (!key) return
    headers[key] = row.value != null ? String(row.value) : ''
  })
  const json = {
    method: normalized.method,
    url: normalized.url,
    body,
    contentType: 'application/json',
  }
  if (Object.keys(headers).length) {
    json.headers = headers
  }
  return json
}

function objectToHeaderRows(obj) {
  if (!obj || typeof obj !== 'object') return []
  return Object.keys(obj).map((key) => ({
    name: key,
    value: obj[key] != null ? String(obj[key]) : '',
  }))
}

function objectToInputParamRows(obj) {
  if (!obj || typeof obj !== 'object') return []
  return Object.keys(obj).map((key) => ({
    name: key,
    value: obj[key] != null ? String(obj[key]) : '',
    description: '',
  }))
}

/** 按当前列表/弹窗编辑模式准备表单数据（表单模式可从 interface_json 回填） */
export function resolveExternalApiItemForEdit(item, editMode) {
  const normalized = normalizeExternalApiItem(item)
  if (editMode !== EXTERNAL_API_MODE.FORM) {
    return normalized
  }

  const json =
    normalized.interface_json && typeof normalized.interface_json === 'object'
      ? normalized.interface_json
      : {}
  const methodCandidate = String(json.method || normalized.method || 'POST').toUpperCase()
  const method = HTTP_METHODS.includes(methodCandidate) ? methodCandidate : normalized.method
  const url = String(normalized.url || '').trim() || (json.url != null ? String(json.url) : '')

  let headers = normalized.headers
  if (!headers.length && json.headers && typeof json.headers === 'object') {
    headers = objectToHeaderRows(json.headers)
  }

  let inputParams = normalized.input_params
  if (!inputParams.length && json.body && typeof json.body === 'object') {
    inputParams = objectToInputParamRows(json.body)
  }

  return {
    ...normalized,
    mode: EXTERNAL_API_MODE.FORM,
    method,
    url,
    headers,
    input_params: inputParams,
  }
}

/** 列表展示名称 */
export function getExternalApiDisplayName(item) {
  const name = item && item.name != null ? String(item.name).trim() : ''
  return name || '未命名'
}

/** 表单模式：输入参数列展示 */
export function formatInputParamsForTable(item) {
  const normalized = normalizeExternalApiItem(item)
  if (normalized.input_params.length) {
    return normalized.input_params
      .map((row) => {
        const name = String(row.name || '').trim()
        const value = String(row.value != null ? row.value : '').trim()
        if (!name) return ''
        return value ? `${name}=${value}` : name
      })
      .filter(Boolean)
      .join('; ')
  }
  const json =
    normalized.mode === EXTERNAL_API_MODE.FORM
      ? buildInterfaceJsonFromForm(normalized)
      : normalized.interface_json
  const body = json && typeof json.body === 'object' && json.body ? json.body : {}
  const pairs = Object.keys(body).map((key) => {
    const value = body[key]
    return value != null && String(value) !== '' ? `${key}=${value}` : key
  })
  return pairs.length ? pairs.join('; ') : '-'
}

/** 表单模式：Header 列展示 */
export function formatHeadersForTable(item) {
  const normalized = normalizeExternalApiItem(item)
  if (normalized.headers.length) {
    const pairs = normalized.headers
      .map((row) => {
        const name = String(row.name || '').trim()
        const value = String(row.value != null ? row.value : '').trim()
        if (!name) return ''
        return value ? `${name}=${value}` : name
      })
      .filter(Boolean)
    return pairs.length ? pairs.join('; ') : '-'
  }
  const json =
    normalized.mode === EXTERNAL_API_MODE.FORM
      ? buildInterfaceJsonFromForm(normalized)
      : normalized.interface_json
  const headers = json && typeof json.headers === 'object' && json.headers ? json.headers : null
  if (!headers || !Object.keys(headers).length) return '-'
  return Object.keys(headers)
    .map((key) => {
      const value = headers[key]
      return value != null && String(value) !== '' ? `${key}=${value}` : key
    })
    .join('; ')
}

/** JSON 模式：接口 JSON 列展示（单行压缩） */
export function stringifyInterfaceJsonCompact(item) {
  const normalized = normalizeExternalApiItem(item)
  const json =
    normalized.mode === EXTERNAL_API_MODE.JSON
      ? normalized.interface_json
      : buildInterfaceJsonFromForm(normalized)
  try {
    return JSON.stringify(json)
  } catch (e) {
    return '{}'
  }
}

/** 列表展示：超长文本截断，末尾加 … */
export function truncateDisplayText(text, maxLength = 80) {
  const raw = text != null ? String(text) : ''
  if (!raw || raw.length <= maxLength) return raw
  return `${raw.slice(0, maxLength)}...`
}

/** JSON 模式：编辑用格式化文本 */
export function stringifyInterfaceJsonPretty(item) {
  const normalized = normalizeExternalApiItem(item)
  const json =
    normalized.mode === EXTERNAL_API_MODE.JSON
      ? normalized.interface_json
      : buildInterfaceJsonFromForm(normalized)
  try {
    return JSON.stringify(json, null, 2)
  } catch (e) {
    return '{}'
  }
}

/** 解析编辑框中的 interface JSON 文本 */
export function parseInterfaceJsonText(text) {
  const raw = text != null ? String(text).trim() : ''
  if (!raw) {
    return { ok: false, message: '请填写接口 JSON' }
  }
  try {
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { ok: false, message: '接口 JSON 须为对象' }
    }
    return { ok: true, value: parsed }
  } catch (e) {
    return { ok: false, message: '接口 JSON 格式不正确' }
  }
}

function migrateLegacyCustomToolsEntry(entry) {
  if (entry == null) return null
  if (typeof entry === 'string') {
    const parsed = parseInterfaceJsonValue(entry)
    return normalizeExternalApiItem({
      mode: EXTERNAL_API_MODE.JSON,
      interface_json: parsed,
    })
  }
  if (typeof entry !== 'object') return null

  if (entry.mode === EXTERNAL_API_MODE.FORM || Array.isArray(entry.input_params) || Array.isArray(entry.headers)) {
    return normalizeExternalApiItem({ ...entry, mode: EXTERNAL_API_MODE.FORM })
  }

  if (entry.interface_json != null) {
    return normalizeExternalApiItem(entry)
  }

  if (entry.url || entry.method) {
    return normalizeExternalApiItem({
      mode: EXTERNAL_API_MODE.JSON,
      name: entry.name != null ? String(entry.name) : '',
      description: entry.description != null ? String(entry.description) : '',
      interface_json: entry,
    })
  }

  return normalizeExternalApiItem({
    mode: EXTERNAL_API_MODE.JSON,
    interface_json: entry,
  })
}

/** 从旧版 custom_tools 字符串迁移 */
export function migrateCustomToolsToExternalApis(customTools) {
  const text = customTools != null ? String(customTools).trim() : ''
  if (!text) return []

  try {
    const parsed = JSON.parse(text)
    if (Array.isArray(parsed)) {
      return parsed.map(migrateLegacyCustomToolsEntry).filter(Boolean)
    }
    if (parsed && typeof parsed === 'object') {
      const one = migrateLegacyCustomToolsEntry(parsed)
      return one ? [one] : []
    }
  } catch (e) {
    const one = normalizeExternalApiItem({
      mode: EXTERNAL_API_MODE.JSON,
      interface_json: { raw: text },
    })
    return [one]
  }
  return []
}

/** 表单 externalApis → llmsettings 单模式列表 */
export function buildExternalApisSnapshot(externalApis, defaultMode = EXTERNAL_API_MODE.JSON) {
  const mode =
    defaultMode === EXTERNAL_API_MODE.FORM ? EXTERNAL_API_MODE.FORM : EXTERNAL_API_MODE.JSON
  return (Array.isArray(externalApis) ? externalApis : [])
    .map((item) => normalizeExternalApiItem({ ...item, mode }))
    .map((item) => {
      if (mode === EXTERNAL_API_MODE.JSON) {
        // JSON 模式：与弹窗一致，只存名称 + 接口 JSON（id 供触发器 api_id 引用）
        return {
          id: item.id,
          name: String(item.name || '').trim(),
          interface_json: item.interface_json,
        }
      }
      // 表单模式：与弹窗字段一一对应，不存派生的 interface_json / mode
      return {
        id: item.id,
        name: String(item.name || '').trim(),
        description: String(item.description || '').trim(),
        method: item.method,
        url: String(item.url || '').trim(),
        headers: item.headers.map((row) => ({
          name: String(row.name || '').trim(),
          value: String(row.value != null ? row.value : ''),
        })),
        input_params: item.input_params.map((row) => ({
          name: String(row.name || '').trim(),
          value: String(row.value != null ? row.value : ''),
          description: String(row.description || '').trim(),
        })),
      }
    })
}

/** JSON / 表单两组接口 → llmsettings */
export function buildExternalApisGroupSnapshot(jsonList, formList) {
  return {
    external_apis_json: buildExternalApisSnapshot(jsonList, EXTERNAL_API_MODE.JSON),
    external_apis_form: buildExternalApisSnapshot(formList, EXTERNAL_API_MODE.FORM),
  }
}

function normalizeExternalApisListForMode(list, mode) {
  return (Array.isArray(list) ? list : []).map((item) =>
    normalizeExternalApiItem({ ...item, mode }),
  )
}

function splitLegacyExternalApis(legacyList) {
  const json = []
  const form = []
  legacyList.forEach((item) => {
    const normalized = normalizeExternalApiItem(item)
    if (normalized.mode === EXTERNAL_API_MODE.FORM) {
      form.push({ ...normalized, mode: EXTERNAL_API_MODE.FORM })
    } else {
      json.push({ ...normalized, mode: EXTERNAL_API_MODE.JSON })
    }
  })
  return { json, form }
}

/** llmsettings → 表单两组接口（兼容 external_apis / custom_tools） */
export function applyExternalApisGroupFromSnapshot(callTab, legacyCustomTools) {
  const tab = callTab && typeof callTab === 'object' ? callTab : {}
  const hasJsonField = Array.isArray(tab.external_apis_json)
  const hasFormField = Array.isArray(tab.external_apis_form)

  if (hasJsonField || hasFormField) {
    return {
      json: normalizeExternalApisListForMode(tab.external_apis_json, EXTERNAL_API_MODE.JSON),
      form: normalizeExternalApisListForMode(tab.external_apis_form, EXTERNAL_API_MODE.FORM),
    }
  }

  const legacyList = applyExternalApisFromSnapshot(tab.external_apis, legacyCustomTools)
  if (!legacyList.length) {
    return { json: [], form: [] }
  }
  return splitLegacyExternalApis(legacyList)
}

/** llmsettings.external_apis → 表单（兼容旧单列表） */
export function applyExternalApisFromSnapshot(raw, legacyCustomTools) {
  if (Array.isArray(raw) && raw.length) {
    return raw.map(normalizeExternalApiItem)
  }
  if (raw && Array.isArray(raw.list) && raw.list.length) {
    return raw.list.map(normalizeExternalApiItem)
  }
  return migrateCustomToolsToExternalApis(legacyCustomTools)
}
